/**
 * IFRAP-AI - Publish Content Dialog
 * Modal to publish validated content to the public front
 */

import React, { useState } from 'react';
import { X, Send, Globe, Tag, Image as ImageIcon, Check, AlertCircle } from 'lucide-react';
import api from '../api/client';

const CONTENT_CATEGORIES = [
  { value: 'economie', label: 'Économie' },
  { value: 'finances_publiques', label: 'Finances publiques' },
  { value: 'energie', label: 'Énergie' },
  { value: 'social', label: 'Social' },
  { value: 'education', label: 'Éducation' },
  { value: 'sante', label: 'Santé' },
  { value: 'retraites', label: 'Retraites' },
  { value: 'collectivites', label: 'Collectivités' },
  { value: 'international', label: 'International' },
  { value: 'general', label: 'Général' },
];

const PublishContentDialog = ({
  isOpen,
  onClose,
  contentType, // 'chronique', 'rapport', 'revue_presse', 'infographie', 'presentation'
  title: initialTitle,
  content,
  sourceId,
  gammaUrl,
}) => {
  const [title, setTitle] = useState(initialTitle || '');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('general');
  const [tags, setTags] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const contentTypeLabels = {
    chronique: 'Chronique média',
    rapport: 'Rapport d\'analyse',
    revue_presse: 'Revue de presse',
    infographie: 'Infographie',
    presentation: 'Présentation Gamma',
  };

  const handlePublish = async () => {
    if (!title.trim() || !summary.trim()) {
      setError('Le titre et le résumé sont obligatoires');
      return;
    }

    setPublishing(true);
    setError(null);

    try {
      const response = await api.post('/api/publications/publish', {
        content_type: contentType,
        title: title.trim(),
        summary: summary.trim(),
        content: content || '',
        category,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        source_id: sourceId,
        gamma_url: gammaUrl,
        image_url: imageUrl || null,
      });

      if (response.data.success) {
        setPublished(true);
        setTimeout(() => {
          onClose(true); // Close with success flag
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="publish-dialog">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#000091] to-[#1a1a6e] p-5 text-white relative">
          <button
            onClick={() => onClose(false)}
            className="absolute top-4 right-4 text-white/60 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Publier sur le front public</h3>
              <p className="text-white/70 text-sm">{contentTypeLabels[contentType] || contentType}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {published ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900">Publié avec succès !</h4>
              <p className="text-gray-500 text-sm mt-1">Votre contenu est maintenant visible sur le front public.</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre de publication *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#000091] focus:border-transparent"
                  placeholder="Titre accrocheur pour le public"
                  data-testid="publish-title"
                />
              </div>

              {/* Summary */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Résumé (chapeau) *
                </label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#000091] focus:border-transparent"
                  placeholder="Résumé court pour l'aperçu (2-3 phrases)"
                  data-testid="publish-summary"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#000091]"
                  data-testid="publish-category"
                >
                  {CONTENT_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Tag className="w-3.5 h-3.5 inline mr-1" />
                  Tags (séparés par des virgules)
                </label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="dette, budget, 2025"
                />
              </div>

              {/* Image URL (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <ImageIcon className="w-3.5 h-3.5 inline mr-1" />
                  Image de couverture (URL)
                </label>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="https://..."
                />
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-700">
                <strong>Note :</strong> Les abonnés aux notifications seront alertés de cette nouvelle publication.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!published && (
          <div className="p-5 pt-0 flex gap-3">
            <button
              onClick={() => onClose(false)}
              className="flex-1 py-2.5 px-4 text-gray-600 font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handlePublish}
              disabled={publishing || !title.trim() || !summary.trim()}
              className="flex-1 py-2.5 px-4 bg-[#000091] text-white font-medium rounded-lg hover:bg-[#000091]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="publish-submit"
            >
              {publishing ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Publication...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Publier
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishContentDialog;
